import { useEffect, useState } from 'react';

import { Select, Stack, Text } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { notifications } from '@mantine/notifications';

import { useUpdatePatientMutation } from '@/store/slices/api/transmissionReport/reportsPatientApiSlice';

import { PAUSED_STATUS_REASONS } from '@/pages/physician/TransmissionReportPage/ReportPatientDetails/DetailsSection/PatientStatusModal/constants';
import CustomModal from '@/ui/Modal';
import { Patient } from '@/types/patient';

interface PausePatientModalProps {
  opened: boolean;
  onClose: () => void;
  patientId: string;
  patientName: string;
}

const REASON_OPTIONS = PAUSED_STATUS_REASONS.map((r) => ({ value: r, label: r }));

export function PausePatientModal({
  opened,
  onClose,
  patientId,
  patientName
}: PausePatientModalProps) {
  const [startDate, setStartDate] = useState<Date | null>(new Date());
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [reason, setReason] = useState<string | null>(null);
  const [updatePatient, { isLoading }] = useUpdatePatientMutation();

  useEffect(() => {
    if (opened) {
      setStartDate(new Date());
      setEndDate(null);
      setReason(null);
    }
  }, [opened]);

  const handleSubmit = async () => {
    if (!endDate || !reason) return;
    try {
      const formatDate = (date: Date) => {
        return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
      };
      await updatePatient({
        patient: {
          id: patientId,
          status: 'Paused' as unknown as Patient['status'],
          statusReason: reason,
          statusStartDate: startDate ? formatDate(startDate) : undefined,
          statusEndDate: formatDate(endDate)
        }
      }).unwrap();
      notifications.show({
        title: 'Patient paused',
        message: `${patientName} has been paused.`,
        color: 'teal'
      });
      onClose();
    } catch {
      notifications.show({ title: 'Error', message: 'Failed to pause patient.', color: 'red' });
    }
  };

  return (
    <CustomModal
      header={{ title: 'Pause Patient' }}
      modalProps={{ opened, onClose, size: 'md' }}
      secondaryButton={{ text: 'Cancel', onClick: onClose }}
      primaryButton={{
        text: 'Pause Patient',
        onClick: handleSubmit,
        isDisabled: !endDate || !reason,
        isLoading
      }}>
      <Stack gap="md" p="md">
        <Text size="sm" c="dimmed">
          Pauses {patientName} until the end date. Scheduled and pending appointments are canceled.
          Open tasks are canceled, except transmission tracking. The pause lifts on the end date.
        </Text>
        <Stack gap={4}>
          <Text size="sm" fw={500}>
            Start Date
          </Text>
          <DatePickerInput
            placeholder="Pick a date"
            value={startDate}
            onChange={setStartDate}
            popoverProps={{ withinPortal: true, zIndex: 400 }}
          />
        </Stack>
        <Stack gap={4}>
          <Text size="sm" fw={500}>
            End Date
          </Text>
          <DatePickerInput
            placeholder="Pick a date"
            value={endDate}
            onChange={setEndDate}
            minDate={startDate ?? undefined}
            popoverProps={{ withinPortal: true, zIndex: 400 }}
          />
        </Stack>
        <Stack gap={4}>
          <Text size="sm" fw={500}>
            Pause Reason
          </Text>
          <Select
            placeholder="Select a reason..."
            data={REASON_OPTIONS}
            value={reason}
            onChange={setReason}
            allowDeselect={false}
          />
        </Stack>
      </Stack>
    </CustomModal>
  );
}
